import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { Banner, Banners } from "../../addons/banners/banners.interface";
import { BannersService } from "../../addons/banners/banners.service";
import { Link, Links } from "../../addons/links/links.interface";
import { LinksService } from "../../addons/links/links.service";
import {
  Disclaimer,
  Disclaimers,
} from "../../addons/disclaimers/disclaimers.interface";
import { DisclaimersService } from "../../addons/disclaimers/disclaimers.service";
import { forkJoin, of, Subject } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-addons",
  templateUrl: "./addons.component.html",
  styleUrls: ["./addons.component.scss"],
  standalone: false,
})
export class AddonsComponent implements OnInit, OnDestroy {
  banners: Banners | null = null;
  links: Links | null = null;
  disclaimers: Disclaimers | null = null;
  isAllDataLoaded = false;

  readonly linkLabel = (link: Link) => link.name || link.url || "";
  readonly linkValue = (link: Link) => link.addonId;
  readonly linkSearch = (link: Link) => `${link.name || ""} ${link.url || ""}`;
  readonly bannerLabel = (banner: Banner) => banner.name || banner.url || "";
  readonly bannerValue = (banner: Banner) => banner.addonId;
  readonly bannerSearch = (banner: Banner) =>
    `${banner.name || ""} ${banner.url || ""}`;
  readonly disclaimerLabel = (disclaimer: Disclaimer) =>
    disclaimer.name || this.extractDisclaimerHtmlContent(disclaimer);
  readonly disclaimerValue = (disclaimer: Disclaimer) => disclaimer.addonId;
  readonly disclaimerSearch = (disclaimer: Disclaimer) =>
    `${disclaimer.name || ""} ${this.extractDisclaimerHtmlContent(disclaimer)}`;

  @Input() form: UntypedFormGroup;
  @Output() ready = new EventEmitter<void>();

  private readonly domParser = new DOMParser();
  private readonly disclaimerContentCache = new Map<string, string>();
  private readonly destroy$ = new Subject<void>();
  private readyEmitted = false;

  constructor(
    private bannersService: BannersService,
    private linksService: LinksService,
    private disclaimersService: DisclaimersService,
  ) {}

  ngOnInit(): void {
    forkJoin({
      banners: this.bannersService
        .getBanners()
        .pipe(catchError(() => of({ banners: [] } as Banners))),
      links: this.linksService
        .getLinks()
        .pipe(catchError(() => of({ links: [] } as Links))),
      disclaimers: this.disclaimersService
        .getDisclaimers()
        .pipe(catchError(() => of({ disclaimers: [] } as Disclaimers))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ banners, links, disclaimers }) => {
          this.banners = banners;
          this.links = links;
          this.disclaimers = disclaimers;
          this.isAllDataLoaded = true;
          this.emitReady();
        },
        error: () => {
          this.banners = { banners: [] };
          this.links = { links: [] };
          this.disclaimers = { disclaimers: [] };
          this.isAllDataLoaded = true;
          this.emitReady();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  extractDisclaimerHtmlContent(d: Disclaimer): string {
    const cached = this.disclaimerContentCache.get(d.addonId);
    if (cached) {
      return cached;
    }

    const htmlContent = this.domParser.parseFromString(
      d.htmlContent,
      "text/html",
    ).documentElement.textContent;

    this.disclaimerContentCache.set(d.addonId, htmlContent);
    return htmlContent;
  }

  private emitReady(): void {
    if (this.readyEmitted) {
      return;
    }
    this.readyEmitted = true;
    this.ready.emit();
  }
}
